import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ui/Toast';
import { Globe, CheckCircle } from 'lucide-react';

export default function WebHostingInterestPage() {
  const navigate = useNavigate();
  const { data: companyId } = useCurrentCompany();
  const { showToast } = useToast();
  const [isInterested, setIsInterested] = useState<boolean | null>(null);
  const [interestType, setInterestType] = useState<'web_hosting' | 'web_enhancements' | 'both' | null>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (isInterested === null) {
      showToast('Please select your interest level', 'error');
      return;
    }

    if (isInterested && !interestType) {
      showToast('Please select an interest type', 'error');
      return;
    }

    if (!companyId) {
      showToast('Company not found', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await (supabase as any)
        .from('web_hosting_interest')
        .upsert({
          company_id: companyId,
          is_interested: isInterested,
          interest_type: isInterested ? interestType : null,
          notes: notes || null,
        }, {
          onConflict: 'company_id,location_id',
        });

      if (error) {
        // If table doesn't exist (migration not applied), show error
        if (error.code === 'PGRST116' || error.message?.includes('does not exist')) {
          showToast('Web hosting interest system not available yet. Migrations may not be applied.', 'error');
          return;
        }
        throw error;
      }

      showToast(
        isInterested
          ? 'Thank you! We\'ll contact you soon about web hosting options.'
          : 'Thank you for your feedback!',
        'success'
      );

      setTimeout(() => {
        navigate('/activity');
      }, 2000);
    } catch (error) {
      console.error('Error saving web hosting interest:', error);
      showToast('Failed to save your interest. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE] flex items-center gap-2">
              <Globe className="w-5 h-5 text-rollops-accent" />
              Web Hosting & Enhancement Interest
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <p className="text-[#A9A3A3] mb-4">
                Are you interested in web hosting or website enhancement services?
              </p>

              {/* Interest Level */}
              <div className="space-y-3 mb-6">
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                  Interest Level
                </label>
                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setIsInterested(true);
                      if (!interestType) setInterestType('both');
                    }}
                    className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                      isInterested === true
                        ? 'border-[#9B1421] bg-[#9B1421]/10'
                        : 'border-[#504A4A] bg-[#363232] hover:border-[#706A6A]'
                    }`}
                  >
                    <CheckCircle className={`w-6 h-6 mx-auto mb-2 ${
                      isInterested === true ? 'text-rollops-accent' : 'text-[#706A6A]'
                    }`} />
                    <span className="text-sm text-[#EBFFEE]">Yes, I&apos;m interested</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsInterested(false);
                      setInterestType(null);
                    }}
                    className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                      isInterested === false
                        ? 'border-[#504A4A] bg-[#363232]'
                        : 'border-[#504A4A] bg-[#363232] hover:border-[#706A6A]'
                    }`}
                  >
                    <span className="text-sm text-[#EBFFEE]">Not right now</span>
                  </button>
                </div>
              </div>

              {/* Interest Type (if interested) */}
              {isInterested === true && (
                <div className="space-y-3 mb-6">
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                    What are you interested in?
                  </label>
                  <select
                    value={interestType || ''}
                    onChange={(e) => setInterestType(e.target.value as 'web_hosting' | 'web_enhancements' | 'both')}
                    className="w-full bg-[#363232] border border-[#504A4A] text-[#EBFFEE] rounded px-3 py-2"
                  >
                    <option value="">Select an option</option>
                    <option value="web_hosting">Web Hosting</option>
                    <option value="web_enhancements">Website Enhancements</option>
                    <option value="both">Both</option>
                  </select>
                </div>
              )}

              {/* Notes */}
              {isInterested === true && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Tell us about your specific needs or requirements..."
                    rows={4}
                    className="w-full bg-[#363232] border border-[#504A4A] text-[#EBFFEE] rounded px-3 py-2 resize-none"
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <Button
                  onClick={() => navigate('/activity')}
                  className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || isInterested === null || (isInterested && !interestType)}
                  className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
  );
}
